import { useCallback, useState, type FC } from 'react';
import { Box, Button, Modal, StatusBanner, Typography } from '@lucid/lego';
import type { ModuleFileReference } from '../../../../../api/services/lupp/order/dto.order';
import { useCreateInvoice } from '../../../../../api/services/lupp/order/order';
import { DOCUMENT_CATEGORY } from '../../../../../constants/constants';
import { NOT_APPLICABLE_FALLBACK } from '../PaymentFinancing/constants';
import Table from './Table/Table';
import styles from './ProForma.module.scss';

type ProFormaProps = {
  orderMasterIdentifier: string | null;
  sfdcDeliveryIdentifier?: string | null;
  invoices?: ModuleFileReference[] | null;
  /** Embedded finance providers don't support Pro-Forma generation */
  isEmbeddedFinance?: boolean;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  locale?: string;
  dataTestId?: string;
};

/**
 * Pro-Forma Invoice component for order details
 * @component
 * @param props - Component props
 * @param props.orderMasterIdentifier - Order master identifier for fetching invoices
 * @param props.isEmbeddedFinance - Shows a not-applicable message instead of the Generate action and history
 * @param props.theme - Theme variant
 * @param props.labels - Localized labels
 * @param props.dataTestId - Test ID for the component
 * @returns Pro-Forma Invoice section
 */
const ProForma: FC<ProFormaProps> = ({
  orderMasterIdentifier,
  sfdcDeliveryIdentifier,
  invoices,
  isEmbeddedFinance = false,
  theme,
  labels,
  locale,
  dataTestId = 'proforma-invoice',
}) => {
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const invertedTheme = theme === 'light' ? 'dark' : 'light';

  const { mutate: createInvoice, isPending: isCreatingInvoice } = useCreateInvoice();

  const proFormaInvoiceLabel = labels?.['proforma.invoice'] || 'Pro-Forma Invoice';
  const generateInvoiceLabel = labels?.['proforma.generateProforma'] || 'Generate Pro-Forma';
  const proFormaHistoryLabel = labels?.['proforma.history'] || 'Pro-Forma History';
  const generatingInvoiceLabel = labels?.['proforma.generatingInvoice'] || 'Generating Invoice';
  const proFormaGeneratingMessage =
    labels?.['proforma.generatingMessage'] ||
    'The Pro-Forma invoice is currently being generated. Please check back shortly.';
  const closeLabel = labels?.['proforma.close'] || 'close';
  const errorModalTitle = labels?.['proforma.errorTitle'] || 'An Error Has Occurred';
  const errorModalMessage =
    labels?.['proforma.errorMessage'] ||
    'An error has occurred while generating your file. Please try again.';

  const handleGenerateInvoice = useCallback(() => {
    if (!orderMasterIdentifier) return;

    createInvoice(
      { orderIdentifier: orderMasterIdentifier, invoiceType: DOCUMENT_CATEGORY.PROFORMA_INVOICE },
      {
        onSuccess: () => setIsGenerateModalOpen(true),
        onError: () => setIsErrorModalOpen(true),
      }
    );
  }, [orderMasterIdentifier, createInvoice]);

  return (
    <Box testId={dataTestId}>
      <Typography variant="subtitle" size="small" theme={theme} color="black">
        {proFormaInvoiceLabel}
      </Typography>
      <Box mt={24}>
        {isEmbeddedFinance ? (
          <StatusBanner
            type="info"
            message={labels?.['financeProvider.notApplicable'] ?? NOT_APPLICABLE_FALLBACK}
            dataTestId="proforma-not-applicable"
          />
        ) : (
          <Button
            variant="button"
            type="secondary"
            color="neutral"
            theme={invertedTheme}
            label={generateInvoiceLabel}
            handleClick={handleGenerateInvoice}
            disabled={isCreatingInvoice || !orderMasterIdentifier}
            gaComponent="orderhub-generate-proforma-invoice"
            dataTestId="generate-proforma-invoice"
          />
        )}
      </Box>
      {!isEmbeddedFinance && (
        <Box className={styles.historySection} mt={64}>
          <Typography variant="subtitle" size="small" theme={theme} color="black">
            {proFormaHistoryLabel}
          </Typography>
          <Table
            theme={theme}
            invoices={invoices || []}
            sfdcDeliveryId={sfdcDeliveryIdentifier || ''}
            labels={labels}
            locale={locale}
            compact
          />
        </Box>
      )}

      <Modal
        dataTestId="generate-proforma-modal"
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        title={generatingInvoiceLabel}
        theme="light"
        size="small"
        footer={
          <Button
            variant="button"
            type="primary"
            color="neutral"
            theme="light"
            label={closeLabel}
            handleClick={() => setIsGenerateModalOpen(false)}
            gaComponent="orderhub-close-generating-invoice-modal"
            dataTestId="close-generate-proforma-modal"
            isFluid
          />
        }
      >
        <Typography variant="body" size="medium" theme="dark" color="black" align="center">
          {proFormaGeneratingMessage}
        </Typography>
      </Modal>

      <Modal
        dataTestId="error-proforma-modal"
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title={errorModalTitle}
        theme="light"
        size="small"
        footer={
          <Button
            variant="button"
            type="primary"
            color="neutral"
            theme="light"
            label={closeLabel}
            handleClick={() => setIsErrorModalOpen(false)}
            gaComponent="orderhub-close-error-invoice-modal"
            dataTestId="close-error-proforma-modal"
            isFluid
          />
        }
      >
        <Typography variant="body" size="medium" theme="dark" color="black" align="center">
          {errorModalMessage}
        </Typography>
      </Modal>
    </Box>
  );
};

ProForma.displayName = 'ProForma';

export default ProForma;
